import { kindText } from '../Fianza/copy'
import type { TFunction } from 'i18next'
import type { StepState } from '@/ds'
import { dayMonth, formatMoney, formatMoneyShort, time, weekdayLong } from '@/lib/format'
import type { KeyStatus } from '@/services/accesos'
import type { Arrival } from '@/services/checkin'
import type { Deposit } from '@/services/fianza'
import type { PaymentSummary } from '@/services/pagos'
import type { Stay } from '@/services/reserva'

export type StepKind = 'registration' | 'payment' | 'deposit' | 'arrival' | 'key'

export interface StayStep {
  id: StepKind
  state: StepState
  title: string
  subtitle: string
  /** Botón propio de la fila (44 px); null → la fila entera navega a `route` */
  actionLabel: string | null
  actionVariant: 'primary' | 'soft' | 'link'
  route: string | null
}

/**
 * Rutas de destino (inventario D16). Un paso pendiente lleva a su flujo; uno completado, a su sección en Reserva
 * (bloque 5: Reserva es el registro estable y Tu estancia no repite sus datos).
 */
export const STEP_ROUTES = {
  checkin: '/checkin',
  arrival: '/checkin/paso/6',
  payments: '/reserva/pagos',
  deposit: '/reserva/fianza',
  reserveDeposit: '/reserva/fianza/reservar',
  key: '/accesos',
  bookingOccupants: '/reserva#ocupantes',
  bookingPayments: '/reserva#pagos',
  bookingDocuments: '/reserva#documentos',
} as const

export interface StepsInput {
  stay: Stay
  /** null si E2 falló; se muestra aparte el error */
  payment: PaymentSummary | null
  deposit: Deposit | null
  /** `unsupported` = el backend no guarda la hora de llegada: la fila no se muestra */
  arrival: Arrival | null | 'unsupported'
  key: KeyStatus | null
  lang: string
  t: TFunction
}

const ORDER: Record<StepState, number> = { progress: 0, pending: 1, done: 2, locked: 3 }

/** "sábado a las 16:00, en metro" */
export function arrivalText(arrival: Arrival, stay: Stay, lang: string, t: TFunction): string {
  const day = weekdayLong(stay.dates.checkIn, lang)
  const slot = arrival.etaSlot
  let when = slot && 'from' in slot ? t('v2.estancia.cabecera.aLas', { day, time: slot.from }) : slot && 'unknown' in slot ? `${day}, ${t('v2.estancia.pasos.llegada.noLoSe').toLowerCase()}` : day
  if (arrival.transport && ['plane_metro', 'train', 'car', 'taxi'].includes(arrival.transport)) when += `, ${t(`v2.estancia.transporte.${arrival.transport}`)}`
  return when
}

/**
 * Lista de pasos "¿qué me falta para entrar?" (8.1). Orden: en curso, pendientes, completados, bloqueados
 * (bundle B1: "el paso en curso va primero; el completado va al final").
 */
export function buildSteps({ stay, payment, deposit, arrival, key, lang, t }: StepsInput): StayStep[] {
  const steps: StayStep[] = []
  const reg = stay.registration
  // La firma se completa dentro del check-in, no como un paso independiente en Inicio.
  const signaturePending = reg.travelers.some((tr) => tr.signaturePending)

  if (stay.flags.autoCheckIn && reg.total > 0) {
    const title = t('v2.estancia.pasos.registro.titulo', { count: reg.total })
    let state: StepState
    let subtitle: string
    if (reg.pending > 0 && reg.pending < reg.total) {
      state = 'progress'
      subtitle = t('v2.estancia.pasos.registro.enCurso', { count: reg.pending, total: reg.total })
    } else if (reg.pending > 0 || reg.holderPending) {
      state = 'pending'
      subtitle = t('v2.estancia.pasos.registro.pendiente', { count: reg.pending || reg.total })
    } else if (reg.dataError) {
      state = 'progress'
      subtitle = t('v2.estancia.pasos.registro.datosIncorrectos')
    } else if (reg.missingData) {
      state = 'progress'
      subtitle = t('v2.estancia.pasos.registro.faltanDatos')
    } else if (signaturePending) {
      state = 'progress'
      subtitle = t('v2.checkin.ocupantes.faltaFirma')
    } else {
      state = 'done'
      subtitle = t('v2.estancia.pasos.registro.completado', { count: reg.total })
    }
    steps.push({ id: 'registration', state, title, subtitle, actionLabel: state === 'done' ? null : t('v2.estancia.pasos.registro.accion'), actionVariant: 'primary', route: state === 'done' ? STEP_ROUTES.bookingOccupants : STEP_ROUTES.checkin })
  }

  if (payment && stay.flags.paidWithGateway) {
    const title = t('v2.estancia.pasos.pago.titulo')
    if (payment.channelValidation.required) {
      steps.push({ id: 'payment', state: 'pending', title, subtitle: t('v2.estancia.pasos.pago.validar', { channel: payment.channelValidation.channelName ?? '' }).trim(), actionLabel: t('v2.estancia.pasos.pago.accionValidar'), actionVariant: 'primary', route: STEP_ROUTES.payments })
    } else if (payment.pending.amount > 0) {
      steps.push({ id: 'payment', state: 'pending', title, subtitle: t('v2.estancia.pasos.pago.pendiente', { amount: formatMoney(payment.pending, lang) }), actionLabel: t('v2.estancia.pasos.pago.accion'), actionVariant: 'primary', route: STEP_ROUTES.payments })
    } else {
      // El importe pagado vive en Reserva; aquí solo el estado.
      steps.push({ id: 'payment', state: 'done', title, subtitle: t('v2.estancia.pasos.pago.completado'), actionLabel: null, actionVariant: 'primary', route: STEP_ROUTES.bookingPayments })
    }
  }

  if (deposit) {
    const title = t('v2.estancia.pasos.fianza.titulo')
    if (deposit.status === 'pending') {
      const amount = formatMoneyShort(deposit.amount, lang)
      // D7: neutro mientras el backend no dé el tipo.
      const subtitle = kindText(deposit.kind, 'pasoPendiente', t, { amount }) ?? t('v2.estancia.pasos.fianza.pendiente', { amount })
      steps.push({ id: 'deposit', state: 'pending', title, subtitle, actionLabel: t('v2.estancia.pasos.fianza.accion'), actionVariant: 'primary', route: STEP_ROUTES.reserveDeposit })
    } else {
      const subtitle = deposit.heldAt ? t('v2.estancia.pasos.fianza.completado', { date: dayMonth(deposit.heldAt, lang) }) : t('v2.estancia.pasos.fianza.completadoSinFecha')
      steps.push({ id: 'deposit', state: 'done', title, subtitle, actionLabel: null, actionVariant: 'primary', route: STEP_ROUTES.deposit })
    }
  }

  if (arrival !== 'unsupported') {
    const title = t('v2.estancia.pasos.llegada.titulo')
    if (arrival) {
      steps.push({ id: 'arrival', state: 'done', title, subtitle: t('v2.estancia.pasos.llegada.completado', { when: arrivalText(arrival, stay, lang, t) }), actionLabel: t('v2.estancia.pasos.llegada.accion'), actionVariant: 'link', route: STEP_ROUTES.arrival })
    } else {
      steps.push({ id: 'arrival', state: 'pending', title, subtitle: t('v2.estancia.pasos.llegada.pendiente'), actionLabel: null, actionVariant: 'primary', route: STEP_ROUTES.arrival })
    }
  }

  steps.sort((a, b) => ORDER[a.state] - ORDER[b.state])

  if (key && key.state === 'locked' && steps.some((s) => s.state !== 'done')) {
    steps.push({ id: 'key', state: 'locked', title: t('v2.estancia.pasos.llave.titulo'), subtitle: t('v2.estancia.pasos.llave.bloqueada'), actionLabel: null, actionVariant: 'primary', route: null })
  }
  return steps
}

/** Pasos que faltan (sin contar la llave bloqueada). */
export const pendingCount = (steps: StayStep[]) => steps.filter((s) => s.state !== 'done' && s.state !== 'locked').length
export const allDone = (steps: StayStep[]) => steps.length > 0 && steps.every((s) => s.state === 'done')
/** Nombres cortos de lo completado, para el resumen "Todo listo". */
export const doneShort = (steps: StayStep[], t: TFunction) => steps.filter((s) => s.state === 'done').map((s) => t(`v2.estancia.pasos.corto.${s.id}`))

/** Chip de validez de la llave: "Activa desde las 16:00" antes de la hora de entrada, "Activa hasta el 6 oct, 11:00" después. */
export function keyValidity(key: KeyStatus, now: Date, lang: string, t: TFunction): string | null {
  if (key.state !== 'active') return null
  if (key.activeFrom && now < new Date(key.activeFrom)) return t('v2.estancia.llave.activaDesde', { time: time(key.activeFrom, lang) })
  if (key.activeUntil) return t('v2.estancia.llave.activaHasta', { when: `${dayMonth(key.activeUntil, lang)}, ${time(key.activeUntil, lang)}` })
  return null
}
